import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import FormEmbed from "@/components/FormEmbed";
import { siteConfig } from "@/lib/site-config";

const QuoteFormContext = createContext<{ openQuoteForm: () => void }>({
  openQuoteForm: () => {},
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openQuoteForm = useCallback(() => setOpen(true), []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeQuoteForm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeQuoteForm]);

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-sm overflow-y-auto overscroll-contain"
            role="dialog"
            aria-modal="true"
            aria-label="Get your free estimate"
          >
            {/* min-h-full + items-center centres short forms but still lets a
                tall one scroll from the top instead of clipping its head. */}
            <div
              className="flex min-h-full items-center justify-center p-4 sm:p-6"
              onClick={(e) => { if (e.target === e.currentTarget) closeQuoteForm(); }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 16 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="relative w-full max-w-lg widget-shell"
              >
                <button
                  onClick={closeQuoteForm}
                  className="absolute top-3 right-3 z-20 h-9 w-9 rounded-full bg-secondary text-secondary-foreground shadow-lg ring-2 ring-card hover:scale-105 transition-transform flex items-center justify-center"
                  aria-label="Close form"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="bg-card px-6 pt-6 pb-2 pr-16">
                  <h3 className="font-heading text-2xl text-foreground font-bold tracking-tight">
                    Get Your Free Estimate
                  </h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    Tell us about your space — we'll reply within hours.
                  </p>
                </div>

                {/* Embedded LeadConnector form — preserved native dimensions */}
                <FormEmbed id={`popup-${siteConfig.widgets.formId}`} className="p-4 sm:p-6" />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </QuoteFormContext.Provider>
  );
};
